import React, { useEffect } from "react";
import AlertRibbon from "../../../common/AlertRibbon";
import { Formik, Field } from "formik";
import {
  FormGroup,
  FormControlLabel,
  Checkbox,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
} from "@mui/material";
import { AddInvestmentFormProps } from "../../types";
import ProjectDropdown from "./form-components/ProjectDropdown";
import QuantityField from "./form-components/QuantityField";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useClientDetails } from "../../context/clientDetailsContext";
import { addInvestmentForm } from "../formSchema";

export const AddInvestmentForm: React.FC<AddInvestmentFormProps> = ({
  open,
  setOpen,
}) => {
  const isMobile = useMediaQuery("(max-width:1110px)");
  const {
    addInvestment: {
      handleSubmit,
      formLoading,
      isEditing,
      setEditing,
      alert,
      setAlert,
    },
    clientId,
  } = useClientDetails();

  useEffect(() => {
    setEditing(open);
  }, [open, setEditing]);

  const handleClose = () => {
    setAlert(null);
    setEditing(false);
    setOpen(false);
  };
  const formInitialValues = {
    "project-name": "",
    quantity: "",
    unit: "",
    note: "",
    notifyClient: true,
  };

  return (
    <>
      <Formik
        initialValues={formInitialValues}
        validationSchema={addInvestmentForm}
        onSubmit={(form) => handleSubmit(form, clientId)}
        validateOnMount
        initialTouched={{
          "project-name": true,
          quantity: true,
          unit: true,
          note: true,
        }}
      >
        {({ handleSubmit, isValid, values, setFieldValue }) => (
          <Dialog
            maxWidth="xl"
            data-testid="investment-add-modal"
            open={isEditing}
          >
            <Box
              sx={{
                width: "40vw",
              }}
            >
              <DialogTitle data-testid="investment-add-title">
                Add Investment
              </DialogTitle>
              <Box component="form" onSubmit={handleSubmit}>
                <DialogContent>
                  {alert && alert.type === "error" && (
                    <AlertRibbon
                      handleClose={() => setAlert(null)}
                      severity={alert.type}
                      message={alert.message}
                      errorCode={alert.errorCode}
                    />
                  )}
                  <FormGroup>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                        maxWidth: "100%",
                      }}
                    >
                      <Field component={ProjectDropdown} />
                      <Field component={QuantityField} />
                    </Box>
                    <Box
                      sx={{
                        maxWidth: isMobile ? "100%" : "70%",
                        marginTop: 2,
                      }}
                    >
                      <TextField
                        id="note"
                        name="note"
                        label="Note"
                        variant="outlined"
                        multiline
                        rows={4}
                        sx={{ width: "100%" }}
                        value={values["note"]}
                        onChange={(e) => {
                          setFieldValue("note", e.target.value);
                        }}
                      />
                    </Box>
                    <Box sx={{ mt: 2 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            data-testid="notify-client-checkbox"
                            checked={values["notifyClient"]}
                            onChange={(e) =>
                              setFieldValue("notifyClient", e.target.checked)
                            }
                          />
                        }
                        label="Notify client"
                      />
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block", ml: 4 }}
                      >
                        Send an email to the client notifying them about this
                        investment
                      </Typography>
                    </Box>
                  </FormGroup>
                </DialogContent>
                <DialogActions>
                  <Box>
                    <Button
                      data-testid="investment-add-cancel-button"
                      onClick={handleClose}
                      sx={{ marginRight: "4px" }}
                    >
                      Cancel
                    </Button>
                    <Button
                      loading={formLoading}
                      disabled={!isValid}
                      type="submit"
                      variant="contained"
                      data-testid="investment-add-save-button"
                    >
                      Save
                    </Button>
                  </Box>
                </DialogActions>
              </Box>
            </Box>
          </Dialog>
        )}
      </Formik>
    </>
  );
};

export default AddInvestmentForm;
